import { z } from "zod";

/**
 * Preview traffic is multiplexed over the authenticated worker socket. It is
 * intentionally separate from the durable job protocol: preview requests are
 * live HTTP exchanges and must never be replayed after either endpoint has
 * gone away.
 */

export const PREVIEW_PROTOCOL_VERSION = 1;
export const PREVIEW_CHUNK_BYTES = 32 * 1024;
export const PREVIEW_MAX_REQUEST_BYTES = 10 * 1024 * 1024;
export const PREVIEW_MAX_RESPONSE_BYTES = 50 * 1024 * 1024;
export const PREVIEW_MAX_HEADER_BYTES = 32 * 1024;
export const PREVIEW_MAX_HEADERS = 100;
export const PREVIEW_MAX_CONCURRENT_REQUESTS = 8;
export const PREVIEW_FLOW_WINDOW_BYTES = PREVIEW_CHUNK_BYTES * 2;
export const PREVIEW_FIRST_BYTE_TIMEOUT_MS = 30_000;
export const PREVIEW_OVERALL_TIMEOUT_MS = 120_000;
export const PREVIEW_SERVICE_TTL_MS = 90_000;
export const PREVIEW_SERVICE_HEARTBEAT_MS = 30_000;
export const PREVIEW_WS_FLOW_WINDOW_BYTES = 256 * 1024;
export const PREVIEW_WS_MAX_MESSAGE_BYTES = PREVIEW_WS_FLOW_WINDOW_BYTES;
export const PREVIEW_WS_MAX_CONCURRENT = 8;
export const PREVIEW_WS_HANDSHAKE_TIMEOUT_MS = 15_000;
export const PREVIEW_WS_IDLE_TIMEOUT_MS = 5 * 60_000;

const Id = z.string().min(1).max(160).regex(/^[A-Za-z0-9._:-]+$/);
const HeaderTuple = z.tuple([
  z.string().min(1).max(256),
  z.string().max(8_192),
]);

export const PreviewServiceRegistration = z.object({
  id: Id,
  worktreeId: z.string().min(1).max(160),
  name: z.string().min(1).max(100),
  protocol: z.enum(["http", "https"]).default("http"),
  /** Only an integer loopback port is accepted; a host or URL is never part of
   * the wire format, making arbitrary-host SSRF unrepresentable. */
  port: z.number().int().min(1).max(65_535),
  /** Where a preview opens. Each preview has an origin of its own, so request
   * paths reach the local service unchanged; this is only the landing path. */
  basePath: z.string().startsWith("/").max(1_024).default("/"),
});
export type PreviewServiceRegistration = z.infer<typeof PreviewServiceRegistration>;

/** Public service metadata safe to return to an authenticated client. */
export const PreviewService = PreviewServiceRegistration.omit({ port: true }).extend({
  expiresAt: z.number().int().nonnegative(),
  websocket: z.literal(true),
});
export type PreviewService = z.infer<typeof PreviewService>;

/**
 * A preview is served from its own origin, `https://<label>-preview.rivets.dev`,
 * never from the app origin, and that origin never sees the app's session.
 * A client signed in to the app asks for a one-time ticket, then opens `url`
 * (an iframe or a WebView): the preview host redeems the ticket once, sets its
 * own host-only cookie, and redirects to the service's landing path.
 */
export const PREVIEW_BOOTSTRAP_PATH = "/__rivets/bootstrap";
/** How long a minted ticket stays redeemable. It is meant to be opened at once. */
export const PREVIEW_TICKET_TTL_MS = 60_000;

/** `POST /api/worktrees/:worktreeId/previews/:serviceId/ticket`. */
export const PreviewTicketResponse = z.object({
  /** The bootstrap URL on the preview origin, carrying the one-time ticket. */
  url: z.string().url(),
  /** The preview's own origin; every navigation that stays on it is the preview. */
  origin: z.string().url(),
  /** When the ticket stops being redeemable, in epoch milliseconds. */
  expiresAt: z.number().int().nonnegative(),
});
export type PreviewTicketResponse = z.infer<typeof PreviewTicketResponse>;

export const PreviewWorkerToServer = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("preview.services"),
    version: z.literal(PREVIEW_PROTOCOL_VERSION),
    services: z.array(PreviewServiceRegistration).max(100),
  }),
  z.object({
    type: z.literal("preview.response.start"),
    requestId: Id,
    status: z.number().int().min(100).max(599),
    headers: z.array(HeaderTuple).max(PREVIEW_MAX_HEADERS),
  }),
  z.object({
    type: z.literal("preview.response.chunk"),
    requestId: Id,
    sequence: z.number().int().nonnegative(),
    data: z.string().max(Math.ceil(PREVIEW_CHUNK_BYTES * 4 / 3) + 8),
    final: z.boolean(),
  }),
  z.object({
    type: z.literal("preview.response.error"),
    requestId: Id,
    code: z.enum([
      "bad_gateway",
      "body_too_large",
      "headers_too_large",
      "redirect_blocked",
      "service_unavailable",
      "timeout",
      "transport_error",
    ]),
    message: z.string().min(1).max(500),
  }),
  /** Credit granted after the local request-body stream has absorbed bytes. */
  z.object({
    type: z.literal("preview.request.credit"),
    requestId: Id,
    bytes: z.number().int().positive().max(PREVIEW_FLOW_WINDOW_BYTES),
  }),
  z.object({
    type: z.literal("preview.ws.opened"),
    sessionId: Id,
    protocol: z.string().max(256).optional(),
    browserCredit: z.number().int().positive().max(PREVIEW_WS_FLOW_WINDOW_BYTES),
  }),
  z.object({
    type: z.literal("preview.ws.data"),
    sessionId: Id,
    sequence: z.number().int().nonnegative(),
    opcode: z.enum(["text", "binary"]),
    data: z.string().max(Math.ceil(PREVIEW_WS_MAX_MESSAGE_BYTES * 4 / 3) + 8),
    byteLength: z.number().int().nonnegative().max(PREVIEW_WS_MAX_MESSAGE_BYTES),
  }),
  /** Credit for browser -> local-service messages. */
  z.object({
    type: z.literal("preview.ws.credit"),
    sessionId: Id,
    bytes: z.number().int().positive().max(PREVIEW_WS_FLOW_WINDOW_BYTES),
  }),
  z.object({
    type: z.literal("preview.ws.close"),
    sessionId: Id,
    code: z.number().int().min(1000).max(4999),
    reason: z.string().max(123),
  }),
]);
export type PreviewWorkerToServer = z.infer<typeof PreviewWorkerToServer>;

export const PreviewServerToWorker = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("preview.request.start"),
    requestId: Id,
    serviceId: Id,
    method: z.enum(["GET", "HEAD", "POST", "PUT", "PATCH", "DELETE", "OPTIONS"]),
    path: z.string().startsWith("/").max(8_192),
    headers: z.array(HeaderTuple).max(PREVIEW_MAX_HEADERS),
    hasBody: z.boolean(),
  }),
  z.object({
    type: z.literal("preview.request.chunk"),
    requestId: Id,
    sequence: z.number().int().nonnegative(),
    data: z.string().max(Math.ceil(PREVIEW_CHUNK_BYTES * 4 / 3) + 8),
    final: z.boolean(),
  }),
  z.object({
    type: z.literal("preview.request.cancel"),
    requestId: Id,
    reason: z.string().min(1).max(200),
  }),
  /** Credit granted after the edge response stream has absorbed bytes. */
  z.object({
    type: z.literal("preview.response.credit"),
    requestId: Id,
    bytes: z.number().int().positive().max(PREVIEW_FLOW_WINDOW_BYTES),
  }),
  z.object({
    type: z.literal("preview.ws.open"),
    sessionId: Id,
    serviceId: Id,
    path: z.string().startsWith("/").max(8_192),
    headers: z.array(HeaderTuple).max(PREVIEW_MAX_HEADERS),
    protocols: z.array(z.string().min(1).max(256)).max(10),
  }),
  z.object({
    type: z.literal("preview.ws.data"),
    sessionId: Id,
    sequence: z.number().int().nonnegative(),
    opcode: z.enum(["text", "binary"]),
    data: z.string().max(Math.ceil(PREVIEW_WS_MAX_MESSAGE_BYTES * 4 / 3) + 8),
    byteLength: z.number().int().nonnegative().max(PREVIEW_WS_MAX_MESSAGE_BYTES),
  }),
  /** Credit for local-service -> browser messages. */
  z.object({
    type: z.literal("preview.ws.credit"),
    sessionId: Id,
    bytes: z.number().int().positive().max(PREVIEW_WS_FLOW_WINDOW_BYTES),
  }),
  z.object({
    type: z.literal("preview.ws.close"),
    sessionId: Id,
    code: z.number().int().min(1000).max(4999),
    reason: z.string().max(123),
  }),
]);
export type PreviewServerToWorker = z.infer<typeof PreviewServerToWorker>;

export function previewHeaderBytes(headers: readonly (readonly [string, string])[]): number {
  const encoder = new TextEncoder();
  return headers.reduce((total, [name, value]) =>
    total + encoder.encode(name).byteLength + encoder.encode(value).byteLength + 4, 0);
}
